import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button } from './Button';
interface DateRangePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (startDate: Date | null, endDate: Date | null) => void;
  startDate: Date | null;
  endDate: Date | null;
}
export function DateRangePicker({
  isOpen,
  onClose,
  onSelect,
  startDate,
  endDate
}: DateRangePickerProps) {
  const [selectedStart, setSelectedStart] = useState<Date | null>(startDate);
  const [selectedEnd, setSelectedEnd] = useState<Date | null>(endDate);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return {
      daysInMonth,
      startingDayOfWeek
    };
  };
  const {
    daysInMonth,
    startingDayOfWeek
  } = getDaysInMonth(currentMonth);
  const handleDayClick = (day: number) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (!selectedStart || selectedStart && selectedEnd) {
      setSelectedStart(clickedDate);
      setSelectedEnd(null);
    } else if (selectedStart && !selectedEnd) {
      if (clickedDate < selectedStart) {
        setSelectedEnd(selectedStart);
        setSelectedStart(clickedDate);
      } else {
        setSelectedEnd(clickedDate);
      }
    }
  };
  const handleApply = () => {
    onSelect(selectedStart, selectedEnd);
    onClose();
  };
  const handleClear = () => {
    setSelectedStart(null);
    setSelectedEnd(null);
  };
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const isDateInRange = (day: number) => {
    if (!selectedStart) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (selectedEnd) {
      return date >= selectedStart && date <= selectedEnd;
    }
    return date.getTime() === selectedStart.getTime();
  };
  return <AnimatePresence data-id="element-176">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" data-id="element-177" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[85vh]" data-id="element-178">
            <div className="flex items-center justify-between px-6 py-4 border-b border-sand/50" data-id="element-179">
              <h2 className="text-xl font-bold text-charcoal" data-id="element-180">Select Dates</h2>
              <button onClick={onClose} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-181">
                <X size={24} className="text-charcoal" data-id="element-182" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-183">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4" data-id="element-184">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-185">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" data-id="element-186">
                    <path d="M12 16L6 10L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-187" />
                  </svg>
                </button>
                <span className="text-lg font-semibold text-charcoal" data-id="element-188">
                  {monthNames[currentMonth.getMonth()]}{' '}
                  {currentMonth.getFullYear()}
                </span>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-189">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" data-id="element-190">
                    <path d="M8 4L14 10L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-191" />
                  </svg>
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2" data-id="element-192">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-center text-xs font-medium text-moss-gray py-2" data-id="element-193">
                    {day}
                  </div>)}

                {Array.from({
              length: startingDayOfWeek
            }).map((_, i) => <div key={`empty-${i}`} data-id="element-194" />)}

                {Array.from({
              length: daysInMonth
            }).map((_, i) => {
              const day = i + 1;
              const inRange = isDateInRange(day);
              return <button key={day} onClick={() => handleDayClick(day)} className={`aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-all ${inRange ? 'bg-dark-green text-white' : 'text-charcoal hover:bg-sand/30'}`} data-id="element-195">
                      {day}
                    </button>;
            })}
              </div>

              {/* Selected Range Display */}
              {selectedStart && <div className="mt-4 p-4 bg-sand/20 rounded-ios" data-id="element-196">
                  <div className="text-sm text-moss-gray mb-1" data-id="element-197">
                    Selected Range:
                  </div>
                  <div className="text-base font-medium text-charcoal" data-id="element-198">
                    {selectedStart.toLocaleDateString()}
                    {selectedEnd && ` - ${selectedEnd.toLocaleDateString()}`}
                  </div>
                </div>}
            </div>

            <div className="px-6 py-4 border-t border-sand/50 space-y-3" data-id="element-199">
              <div className="flex gap-3" data-id="element-200">
                <Button variant="secondary" onClick={handleClear} className="flex-1" data-id="element-201">
                  Clear
                </Button>
                <Button variant="primary" onClick={handleApply} className="flex-1" data-id="element-202">
                  Apply
                </Button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}